import { Button, Grid, GridItem, Link, TextField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../../hooks/useSaveCoverableInformation';
import { useState } from 'react';
import { validateUVPRegNumber } from '../../../../../utils/coverablesUtil';
import { useHippoText } from '../../../../../hooks/useHippoText';
import { useGetDMRCase } from '../../../../../hooks/useGetDMRInfo';
import { useUpdateCoverableInformation } from '../../../../../recoil/hooks/useUpdateCoverableInformation';
import CaravanInfoBox from './CaravanInfoBox';
import type { CaravanOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import { track } from '../../../../../utils/trackingUtil';
import { currentOnsaCoverableSelector } from '../../../../../recoil/selectors/currentOnsaCoverableSelector';

const CARAVAN_TYPE = 'Campingvogn';

const CalculatePriceForCaravanStep = ({
    goNext,
    goPrevious,
    renderNoRegNumberPage
}: {
    goNext: () => void;
    goPrevious: () => void;
    renderNoRegNumberPage: () => void;
}): React.JSX.Element => {
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { getDMRCase } = useGetDMRCase();
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const coverable = useRecoilValue<CaravanOnsaDTO>(currentOnsaCoverableSelector);
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [registrationNumber, setRegistrationNumber] = useState<string>(coverable.regNumber ?? '');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDMRLoading, setIsDMRLoading] = useState<boolean>(false);
    const [isRegNumberValid, setIsRegNumberValid] = useState<boolean | undefined>(
        coverable.regNumber ? validateUVPRegNumber(registrationNumber) : undefined
    );
    const [isCaravanFound, setIsCaravanFound] = useState<boolean | undefined>(
        coverable.regNumber ? true : undefined
    );

    const fetchDMRInformation = async (registrationNumber: string): Promise<void> => {
        setIsDMRLoading(true);
        await getDMRCase(registrationNumber).then((dmrData) => {
            const { model, make, variant, type } = dmrData;
            if (model && make && variant && type === CARAVAN_TYPE) {
                updateCoverableInformation({
                    ...dmrData,
                    isAddedInOnsa: coverable.isAddedInOnsa
                });
                setIsCaravanFound(true);
            } else {
                setIsCaravanFound(false);
            }
        });
        setIsDMRLoading(false);
    };

    const onNext = (): void => {
        setIsLoading(true);
        updateCoverableInformation({
            name: `${coverable.make ?? ''} ${coverable.model ?? ''}, ${registrationNumber}`,
            regNumber: registrationNumber
        });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            caravan: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                regNumber: registrationNumber
            },
            productToUpdate: currentProduct.flavourName
        };
        void saveCoverableInformation(updatedCoverable).then(() => {
            track(
                `${currentProduct.flavourName}CoverableInformationCaravanInformationWithLicensePlate`,
                [
                    { registrationNumber: coverable.regNumber },
                    { make: coverable.make },
                    { model: coverable.model },
                    { modelYear: coverable.year },
                    { totalWeight: coverable.totalWeight }
                ]
            );
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!isRegNumberValid || !isCaravanFound || isLoading || isDMRLoading}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate>
                <Grid>
                    <GridItem colStart={1} span={8}>
                        <p>
                            {getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-CARAVAN-LICENSE-PLATE-SUBTITLE'
                            )}
                        </p>
                        <TextField
                            label={getHippoText(
                                'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-ENTER-LICENSE-PLATE'
                            )}
                            value={registrationNumber}
                            onChange={(event) => {
                                setRegistrationNumber(event.target.value?.toUpperCase());
                                setIsRegNumberValid(validateUVPRegNumber(event.target.value));
                            }}
                            isValid={isRegNumberValid && isCaravanFound}
                            disabled={isLoading || isDMRLoading}
                            errorText="Registreringsnr. er ikke gyldigt"
                            minLength={6}
                            maxLength={6}
                        />
                        <div className="u-display-flex@md u-flex-justify-between">
                            <Button
                                className="u-spacing-stack-m"
                                disabled={!isRegNumberValid || isLoading || isDMRLoading}
                                isLoading={isDMRLoading}
                                onClick={() => {
                                    void fetchDMRInformation(registrationNumber);
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-FIND-CARAVAN'
                                )}
                            </Button>

                            <Link
                                theme="logo"
                                className={
                                    isLoading || isDMRLoading
                                        ? 'u-spacing-stack-top-6 u-color-supplement-grey-medium'
                                        : 'u-spacing-stack-top-6'
                                }
                                onClick={() => {
                                    if (isLoading || isDMRLoading) return;
                                    renderNoRegNumberPage();
                                }}
                            >
                                {getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LICENSE-PLATE-DONT-KNOW-LICENSE-PLATE'
                                )}
                            </Link>
                        </div>
                    </GridItem>
                    <GridItem span={4}>
                        <CaravanInfoBox isCaravanFound={isCaravanFound} coverable={coverable} />
                    </GridItem>
                </Grid>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default CalculatePriceForCaravanStep;
